import React, {
    Fragment,
    useRef,
    useState,
    useEffect,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { GoogleMap } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

import { useToast } from "../../../components/ui/ToastProvider";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Textarea from "../../../components/ui/Textarea";
import Switch from "../../../components/ui/Switch";
import FriendlyDateSelector from "../../../components/ui/FriendlyDateSelector";
import MapLocationSearchInput from "../../../components/ui/MapLocationSearchInput";
import { PhoneIcon, UserIcon, VkInputIcon, CheckFilledIcon, CheckCircleFilledIcon, ExclamationFilledIcon } from "../../pets/pages/ReportDetailIcons";
import { isValidRussianPhone, isValidVk } from "../../../utils/validators";

// API services
import {
    listMyPets,
    updatePetReport,
    attachPhotoToReport,
    removePhotoFromReport,
    processPetReport,
    createLostPetReport
} from "../../pets/data/pets.service";

import { saveDraft, getDraft, deleteDraft } from "../../../services/draftService";
import { uploadToActiveStorage } from "../../../services/directUploadService";
import { BASE_URL } from "../../../services/api";
import { resizeImageFile } from "../../../utils/resizeImageFile";
import PetMapPinIcon from "../../pets/components/cards/PetMapPinIcon";
import ReportSuccessStep from "./ReportSuccessStep";
import ReportProcessingErrorOverlay from "./ReportProcessingErrorOverlay";
import { subscribeToPetReport } from "../../../services/cable";

const MOSCOW_COORDS = { lat: 55.7558, lng: 37.6173 };
const MADRID_COORDS = { lat: 40.4168, lng: -3.7038 };
const LONDON_COORDS = { lat: 51.5074, lng: -0.1278 };

export default function ReportLostPetDialog({
    open = false,
    onClose = () => { },
    existingReport = null,
    initialStep = 1,
}) {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const initialFocusRef = useRef(null);
    const mapRef = useRef(null);
    const geocoderRef = useRef(null);
    const reverseGeocodeTimeoutRef = useRef(null);
    const isProgrammaticPanRef = useRef(false);
    const isInitialMount = useRef(true);
    const activeSubscriptionRef = useRef(null);
    const { show } = useToast();

    // Clean up ActionCable subscription on unmount
    useEffect(() => {
        return () => {
            if (activeSubscriptionRef.current) {
                activeSubscriptionRef.current.unsubscribe();
                activeSubscriptionRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (open) {
            setStep(initialStep);
            setStep6Error(null);
            isInitialMount.current = true;
        }
    }, [open, initialStep]);

    const totalSteps = 6;
    const [step, setStep] = useState(initialStep);
    const [direction, setDirection] = useState('forward');
    const [loading, setLoading] = useState(false);
    const [step6Error, setStep6Error] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [serverReportId, setServerReportId] = useState(null);
    const [processedReport, setProcessedReport] = useState(null);

    const [defaultCenter] = useState(() => {
        const lng = localStorage.getItem('i18nextLng') || i18n.language || 'ru';
        if (lng.startsWith('es')) return MADRID_COORDS;
        if (lng.startsWith('en')) return LONDON_COORDS;
        return MOSCOW_COORDS;
    });

    const [petName, setPetName] = useState("");
    const [occurredDate, setOccurredDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    });
    const [locationLat, setLocationLat] = useState(null);
    const [locationLng, setLocationLng] = useState(null);
    const [locationMunicipality, setLocationMunicipality] = useState("");
    const [locationCountry, setLocationCountry] = useState("");
    const [locationMunicipalityPlaceId, setLocationMunicipalityPlaceId] = useState("");
    const [locationMunicipalitySlug, setLocationMunicipalitySlug] = useState("");

    const [locationAddress, setLocationAddress] = useState("");
    const [locationCountryCode, setLocationCountryCode] = useState("");

    const [myPets, setMyPets] = useState([]);
    const [selectedPet, setSelectedPet] = useState(null);
    const [loadingPets, setLoadingPets] = useState(false);

    const maxPhotos = 4;
    const [photos, setPhotos] = useState([]);

    const [userDescription, setUserDescription] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactNumber, setContactNumber] = useState("");
    const [contactVk, setContactVk] = useState("");
    const [vkCoauthor, setVkCoauthor] = useState(true);
    const [featCollarText, setFeatCollarText] = useState("");
    const [featClothesText, setFeatClothesText] = useState("");
    const [featMicrochipText, setFeatMicrochipText] = useState("");

    const isMapLoaded = !!(window.google && window.google.maps);

    useEffect(() => {
        if (open && isAuthenticated) {
            setLoadingPets(true);
            listMyPets().then(data => { const list = Array.isArray(data) ? data : (data.pets || []); setMyPets(list); }).catch(console.error).finally(() => setLoadingPets(false));

            if (!existingReport) {
                // Reset form for fresh report
                setStep(1);
                setPetName("");
                setOccurredDate(new Date().toISOString().split('T')[0]);
                setLocationLat(null);
                setLocationLng(null);
                setLocationMunicipality("");
                setLocationCountry("");
                setLocationMunicipalityPlaceId("");
                setLocationMunicipalitySlug("");

                setLocationAddress("");
                setLocationCountryCode("");
                setSelectedPet(null);
                setPhotos([]);
                setUserDescription("");
                setContactName("");
                setContactNumber("");
                setContactVk(user?.vk_id ? `@id${user.vk_id}` : "");
                setVkCoauthor(true);
                setFeatCollarText("");
                setFeatClothesText("");
                setFeatMicrochipText("");
                setServerReportId(null);
            }
        }
    }, [open, isAuthenticated, existingReport, user?.vk_id]);

    useEffect(() => {
        if (open && existingReport) {
            const r = existingReport;
            setStep(initialStep);
            setPetName(r.pet?.name || r.pet_name || r.ai_features?.breed || "");
            if (r.pet_id && myPets.length > 0) { const found = myPets.find(p => p.id == r.pet_id); if (found) { setSelectedPet(found); setPetName(found.name); } }
            else if (r.pet) setSelectedPet(r.pet);
            if (r.event_occurred_at) setOccurredDate(r.event_occurred_at.split('T')[0]);
            if (r.location_lat) setLocationLat(Number(r.location_lat));
            if (r.location_lng) setLocationLng(Number(r.location_lng));
            if (r.municipality) setLocationMunicipality(r.municipality);

            if (r.country) {
                setLocationCountry(r.country);
                // If the DB 'country' field is already a 2-letter code, use it.
                if (r.country.length === 2) setLocationCountryCode(r.country);
            }

            if (r.municipality_place_id) setLocationMunicipalityPlaceId(r.municipality_place_id);
            if (r.municipality_slug) setLocationMunicipalitySlug(r.municipality_slug);
            if (r.location_description) setLocationAddress(r.location_description);
            if (r.user_description) setUserDescription(r.user_description);
            if (r.contact_name) setContactName(r.contact_name);
            if (r.contact_number) setContactNumber(r.contact_number);
            if (r.contact_vk) setContactVk(r.contact_vk);
            const features = typeof r.user_features === 'string' ? (JSON.parse(r.user_features) || {}) : (r.user_features || {});
            if (features.collar) setFeatCollarText(features.collar);
            if (features.clothes) setFeatClothesText(features.clothes);
            if (features.microchip) setFeatMicrochipText(features.microchip);
            if (r.vk_coauthor !== undefined) setVkCoauthor(Boolean(r.vk_coauthor));
            else if (features.vk_coauthor !== undefined) setVkCoauthor(Boolean(features.vk_coauthor));
            else setVkCoauthor(true);
            const pData = r.photos || r.photo_urls || (r.post?.photos_urls) || [];
            if (Array.isArray(pData)) {
                setPhotos(pData.map(p => {
                    const isObj = p && typeof p === 'object';
                    const rawUrl = isObj ? p.url : p;
                    if (!rawUrl) return null;
                    let finalUrl = rawUrl.startsWith('http') ? rawUrl : `${(BASE_URL || "").replace(/\/$/, "")}/${rawUrl.replace(/^\//, "")}`;
                    return { id: isObj ? p.id : null, file: null, previewUrl: finalUrl };
                }).filter(Boolean));
            }
        }
    }, [open, existingReport, myPets, initialStep]);

    useEffect(() => {
        if (open && !isAuthenticated) { // Fixed: Only load local draft if NOT authenticated
            const loadDraftSync = async () => {
                const draft = await getDraft('lost');
                if (draft && draft.data) {
                    setStep(1);
                    if (draft.data.petName) setPetName(draft.data.petName);
                    if (draft.data.occurredDate) setOccurredDate(draft.data.occurredDate);
                    if (draft.data.locationLat) setLocationLat(draft.data.locationLat);
                    if (draft.data.locationLng) setLocationLng(draft.data.locationLng);
                    if (draft.data.locationMunicipality) setLocationMunicipality(draft.data.locationMunicipality);
                    if (draft.data.locationAddress) setLocationAddress(draft.data.locationAddress);
                    if (draft.data.locationCountry) setLocationCountry(draft.data.locationCountry);
                    // Ensure local draft also recovers the code
                    if (draft.data.locationCountryCode || (draft.data.locationCountry?.length === 2)) {
                        setLocationCountryCode(draft.data.locationCountryCode || draft.data.locationCountry);
                    }
                    if (draft.data.locationMunicipalityPlaceId) setLocationMunicipalityPlaceId(draft.data.locationMunicipalityPlaceId);
                    if (draft.data.userDescription) setUserDescription(draft.data.userDescription);
                    if (draft.data.contactName) setContactName(draft.data.contactName);
                    if (draft.data.contactNumber) setContactNumber(draft.data.contactNumber);
                    if (draft.data.contactVk) setContactVk(draft.data.contactVk);
                    if (draft.data.vkCoauthor !== undefined) setVkCoauthor(Boolean(draft.data.vkCoauthor));
                    else setVkCoauthor(true);
                    if (draft.data.featCollarText) setFeatCollarText(draft.data.featCollarText);
                    if (draft.data.featClothesText) setFeatClothesText(draft.data.featClothesText);
                    if (draft.data.featMicrochipText) setFeatMicrochipText(draft.data.featMicrochipText);
                    if (draft.photos && Array.isArray(draft.photos)) {
                        setPhotos(draft.photos.map(file => ({ file, previewUrl: URL.createObjectURL(file) })));
                    }
                }
            };
            loadDraftSync();
        }
    }, [open, isAuthenticated]); // Added isAuthenticated dependency

    const persistDraft = async (targetStep, overrides = {}) => {
        const currentData = {
            petName, occurredDate, locationLat, locationLng,
            locationMunicipality: overrides.locationMunicipality ?? locationMunicipality,
            locationCountry: overrides.locationCountry ?? locationCountry,
            locationMunicipalityPlaceId: overrides.locationMunicipalityPlaceId ?? locationMunicipalityPlaceId,
            locationMunicipalitySlug: overrides.locationMunicipalitySlug ?? locationMunicipalitySlug,

            locationCountryCode: overrides.locationCountryCode ?? locationCountryCode,
            locationAddress: overrides.locationAddress ?? locationAddress,
            userDescription, contactName, contactNumber, contactVk, vkCoauthor, featCollarText, featClothesText, featMicrochipText
        };

        if (isAuthenticated) {
            // Use existing report ID or the one we just created
            const targetId = existingReport?.id || serverReportId;

            const effectiveVkCoauthor = Boolean(contactVk?.trim() && isValidVk(contactVk) && vkCoauthor);

            if (targetId) {
                // UPDATE existing
                try {
                    await updatePetReport(targetId, {
                        pet_id: selectedPet?.id,
                        event_occurred_at: occurredDate,
                        location_lat: locationLat,
                        location_lng: locationLng,
                        location_description: overrides.locationAddress ?? locationAddress,
                        municipality: overrides.locationMunicipality ?? locationMunicipality,

                        municipality_place_id: overrides.locationMunicipalityPlaceId ?? locationMunicipalityPlaceId,
                        municipality_slug: overrides.locationMunicipalitySlug ?? locationMunicipalitySlug,
                        // Ensure we send a code if we have it, either from locationCountryCode or as a fallback from locationCountry
                        country: (overrides.locationCountryCode ?? locationCountryCode) || ((overrides.locationCountry ?? locationCountry)?.length === 2 ? (overrides.locationCountry ?? locationCountry) : undefined),
                        user_description: userDescription,
                        contact_name: contactName,
                        contact_number: contactNumber,
                        contact_vk: contactVk,
                        vk_coauthor: effectiveVkCoauthor,
                        user_features: { collar: featCollarText, clothes: featClothesText, microchip: featMicrochipText, vk_coauthor: effectiveVkCoauthor }
                    });
                    return targetId;
                } catch (err) { console.error("[LostDialog] Failed sync:", err); return targetId; }
            } else {
                // CREATE new draft if we have minimum requirements
                // Only create if we are advancing past step 1
                if (step === 1 && (!targetStep || targetStep <= 1)) return null;

                let finalPetId = selectedPet?.id || null;
                    try {
                        const { data } = await createLostPetReport({
                            petId: finalPetId,
                            petName: petName.trim() || undefined,
                            eventOccurredAt: occurredDate,
                            locationLat: locationLat || 0,
                            locationLng: locationLng || 0,
                            locationDescription: overrides.locationAddress ?? locationAddress,
                            locationMunicipality: overrides.locationMunicipality ?? locationMunicipality,
                            locationCountry: overrides.locationCountry ?? locationCountry,
                            locationMunicipalityPlaceId: overrides.locationMunicipalityPlaceId ?? locationMunicipalityPlaceId,
                            locationMunicipalitySlug: overrides.locationMunicipalitySlug ?? locationMunicipalitySlug,
                            locationCountryCode: (overrides.locationCountryCode ?? locationCountryCode) || ((overrides.locationCountry ?? locationCountry)?.length === 2 ? (overrides.locationCountry ?? locationCountry) : undefined),
                            userDescription: userDescription,
                            contactName: contactName,
                            contactNumber: contactNumber,
                            contactVk: contactVk,
                            vkCoauthor: effectiveVkCoauthor
                        });

                        const newReport = data?.pet_report || data;
                        if (newReport?.id) {
                            setServerReportId(newReport.id);
                            return newReport.id;
                        }
                    } catch (e) {
                        console.error("Failed to create lost report draft", e);
                        return null;
                    }
            }
        } else {
            // Guest -> Local Draft
            const photoFiles = photos.filter(p => !p.status || p.status === 'success').map(p => p.file).filter(f => f);
            await saveDraft('lost', { step: targetStep || step, data: currentData, photos: photoFiles });
        }
    };

    const handleClose = () => {
        if (loading) return;
        // Only save draft if we have not succeeded and advanced past step 1 or effectively have a report already
        if (!showSuccess && (step > 1 || serverReportId || existingReport)) {
            persistDraft(step);
        }
        setShowSuccess(false);
        setProcessedReport(null);
        setStep6Error(null);
        onClose();
        setServerReportId(null); // Reset
    };

    const handleNext = async () => {
        if (step === 1 && !step1Valid) return;
        isInitialMount.current = false;
        setDirection('forward');
        if (step === 2) {
            if (!locationLat || !locationLng) return;
            setLoading(true);
            try {
                const result = await fetchGeocodeData(locationLat, locationLng);
                setLocationMunicipality(result.municipality); setLocationAddress(result.address); setLocationCountry(result.country); setLocationCountryCode(result.countryCode); setLocationMunicipalityPlaceId(result.placeId); setLocationMunicipalitySlug(result.slug);
                await persistDraft(3, { locationMunicipality: result.municipality, locationAddress: result.address, locationCountry: result.country, locationCountryCode: result.countryCode, locationMunicipalityPlaceId: result.placeId, locationMunicipalitySlug: result.slug });
                setStep(3);
            } catch (error) { setStep(3); }
            finally { setLoading(false); }
            return;
        }
        if (step === 3 && !step3Valid) return;
        if (step === 4 && !step4Valid) return;
        if (step === 5 && !step5Valid) return;
        setStep(Math.min(step + 1, totalSteps)); await persistDraft(step + 1);
    };

    const handleBack = () => {
        isInitialMount.current = false;
        setDirection('backward');
        setStep(Math.max(step - 1, 1));
        persistDraft(step - 1);
    };

    useEffect(() => { if (isMapLoaded && !geocoderRef.current && window.google?.maps?.Geocoder) { geocoderRef.current = new window.google.maps.Geocoder(); } }, [isMapLoaded]);

    useEffect(() => {
        if (step === 2 && mapRef.current && window.google?.maps) {
            const timer = setTimeout(() => {
                window.google.maps.event.trigger(mapRef.current, 'resize');
                if (locationLat && locationLng) {
                    mapRef.current.panTo({ lat: locationLat, lng: locationLng });
                }
            }, 260);
            return () => clearTimeout(timer);
        }
    }, [step, locationLat, locationLng]);

    const fetchGeocodeData = (lat, lng) => {
        return new Promise((resolve) => {
            if (!geocoderRef.current) { resolve({ municipality: "", address: "", country: "", countryCode: "", placeId: "" }); return; }
            geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
                if (status !== "OK" || !results || !results.length) { resolve({ municipality: "", address: "", country: "", countryCode: "", placeId: "" }); return; }
                const result = results[0]; const components = result.address_components || [];
                const getComp = (types) => components.find(c => types.some(t => c.types.includes(t)));
                const cityComp = getComp(["locality", "postal_town"]) || getComp(["sublocality", "sublocality_level_1"]) || getComp(["administrative_area_level_2"]);
                const cityName = cityComp?.long_name; const countryComp = getComp(["country"]); const finalMunicipalityName = cityName || result.formatted_address || "";
                resolve({ municipality: finalMunicipalityName, address: result.formatted_address || "", country: countryComp?.long_name || "", countryCode: countryComp?.short_name || "", placeId: result.place_id || "", slug: finalMunicipalityName });
            });
        });
    };

    const handleSelectPlace = ({ address, lat, lng, municipality, country, countryCode, placeId, slug }) => {
        setLocationAddress(address);
        setLocationLat(lat);
        setLocationLng(lng);
        if (municipality) setLocationMunicipality(municipality);
        if (country) setLocationCountry(country);
        if (countryCode) setLocationCountryCode(countryCode);
        if (placeId) setLocationMunicipalityPlaceId(placeId);
        if (slug) setLocationMunicipalitySlug(slug);

        if (mapRef.current) {
            isProgrammaticPanRef.current = true;
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(16);
        }
    };

    const handleMapIdle = () => {
        if (!mapRef.current) return;
        const c = mapRef.current.getCenter();
        if (!c) return;

        const lat = c.lat();
        const lng = c.lng();

        if (lat !== locationLat || lng !== locationLng) {
            setLocationLat(lat);
            setLocationLng(lng);
        }

        if (isProgrammaticPanRef.current) {
            isProgrammaticPanRef.current = false;
            return;
        }

        if (reverseGeocodeTimeoutRef.current) {
            clearTimeout(reverseGeocodeTimeoutRef.current);
        }

        reverseGeocodeTimeoutRef.current = setTimeout(async () => {
            const result = await fetchGeocodeData(lat, lng);
            if (result && result.address) {
                setLocationAddress(result.address);
                if (result.municipality) setLocationMunicipality(result.municipality);
                if (result.country) setLocationCountry(result.country);
                if (result.countryCode) setLocationCountryCode(result.countryCode);
                if (result.placeId) setLocationMunicipalityPlaceId(result.placeId);
                if (result.slug) setLocationMunicipalitySlug(result.slug);
            }
        }, 350);
    };

    const handleTempPhotoChange = async (incoming) => {
        const rawFiles = (Array.isArray(incoming) ? incoming : [incoming])
            .map(item => item instanceof File ? item : item?.file)
            .filter(Boolean);

        if (rawFiles.length === 0) return;

        const remainingSlots = Math.max(0, maxPhotos - photos.length);
        const filesToProcess = rawFiles.slice(0, remainingSlots);
        if (filesToProcess.length === 0) return;

        const targetId = existingReport?.id || serverReportId;

        for (const file of filesToProcess) {
            const tempId = Date.now() + Math.random();
            try {
                const resizedFile = await resizeImageFile(file, 1024, 1024);
                const previewUrl = URL.createObjectURL(resizedFile);

                if (isAuthenticated && targetId) {
                    setPhotos(prev => [...prev, { file: resizedFile, previewUrl, _tempId: tempId, status: 'uploading' }].slice(0, maxPhotos));

                    const blob = await uploadToActiveStorage(resizedFile);
                    const result = await attachPhotoToReport(targetId, blob.signed_id);

                    setPhotos(prev => {
                        const updated = result.photos.map(p => {
                            const finalUrl = p.url.startsWith('http') ? p.url : `${(BASE_URL || "").replace(/\/$/, "")}/${p.url.replace(/^\//, "")}`;
                            return { id: p.id, file: null, previewUrl: finalUrl, status: 'success' };
                        });
                        return [...updated, ...prev.filter(p => p.status === 'error' || (p.status === 'uploading' && p._tempId !== tempId))].slice(0, maxPhotos);
                    });
                } else {
                    setPhotos(prev => [...prev, { file: resizedFile, previewUrl, _tempId: tempId, status: 'success' }].slice(0, maxPhotos));
                }
            } catch (e) {
                console.error("Error adding photo:", e);
                setPhotos(prev => prev.filter(p => p._tempId !== tempId));
                show({ message: t('common.error_generic'), type: "error" });
            }
        }
    };

    const handleRemovePhoto = async (index) => {
        const p = photos[index];
        const targetId = existingReport?.id || serverReportId;
        if (isAuthenticated && targetId && p.id) { try { await removePhotoFromReport(targetId, p.id); } catch (err) { show({ message: "Failed", type: "error" }); } }
        setPhotos(prev => prev.filter((_, i) => i !== index)); if (!isAuthenticated) persistDraft();
    };

    const handleFinishAndLogin = async () => {
        setLoading(true);
        await persistDraft(6);
        handleClose();
        navigate('/login');
    };

    const handleStartSearch = async () => {
        if (!isAuthenticated) return;

        let targetId = existingReport?.id || serverReportId;
        if (!targetId) {
            targetId = await persistDraft(6);
        }

        if (!targetId) {
            show({ message: t('common.error_generic'), variant: "error" });
            return;
        }

        setLoading(true);
        setStep6Error(null);

        let resolved = false;
        let timeoutId = null;

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (activeSubscriptionRef.current) {
                activeSubscriptionRef.current.unsubscribe();
                activeSubscriptionRef.current = null;
            }
        };

        const onReportReady = (reportData) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            setProcessedReport(reportData);
            setLoading(false);
            setStep6Error(null);
            setShowSuccess(true);
        };

        const onReportError = (errorData) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            setLoading(false);
            setShowSuccess(false);

            const reason = errorData?.reason || 'unknown';
            let message = '';

            if (reason === 'invalid_species') {
                message = t('pets.error_invalid_species');
            } else if (reason === 'timeout') {
                message = t('pets.error_processing_timeout');
            } else {
                message = t('pets.error_processing_failed');
            }

            setStep6Error({ reason, message });
            show({ message, variant: 'error' });
        };

        try {
            await persistDraft(6);

            // 1. Subscribe to ActionCable PetReportChannel
            activeSubscriptionRef.current = subscribeToPetReport(targetId, {
                onReceived(data) {
                    console.log("[ActionCable] Step 6 received broadcast:", data);
                    if (data?.event === 'report_processed' && data?.report) {
                        onReportReady(data.report);
                    } else if (data?.event === 'initial_state' && data?.report?.status === 'open') {
                        onReportReady(data.report);
                    } else if (data?.event === 'report_rejected' || data?.event === 'report_failed') {
                        onReportError(data);
                    }
                }
            });

            // 2. Start processing in backend
            await processPetReport(targetId);

            // Safety timeout: 30s without confirmation triggers error instead of false success
            timeoutId = setTimeout(() => {
                if (!resolved) {
                    console.warn("[ActionCable] Processing timed out without confirmation");
                    onReportError({
                        reason: 'timeout'
                    });
                }
            }, 30000);

        } catch (err) {
            console.error(err);
            cleanup();
            setLoading(false);
            const message = t('pets.error_processing_failed');
            setStep6Error({ reason: 'api_error', message });
            show({ message, variant: "error" });
        }
    };

    const getStepHeader = () => {
        switch (step) {
            case 1: return { title: t('pets.lost_empathy_title'), subtitle: t('pets.lost_empathy_subtitle') };
            case 2: return { title: t('pets.report_lost_step_location_title'), subtitle: t('pets.report_lost_step_location_subtitle') };
            case 3: return { title: t('pets.report_lost_step_photos_title'), subtitle: t('pets.pet_photos_desc', { max: maxPhotos }) };
            case 4: return { title: t('pets.contact_info_title', { defaultValue: 'Contact Information' }), subtitle: t('pets.contact_info_subtitle', { defaultValue: 'How can people reach you?' }) };
            case 5: return { title: t('pets.describe_pet'), subtitle: t('pets.describe_pet_placeholder') };
            case 6: return { title: t('pets.step5_title', { name: petName }), subtitle: t('pets.step5_subtitle') };
            default: return { title: "", subtitle: "" };
        }
    };
    const headerContent = getStepHeader();

    const isPhoneFilled = Boolean(contactNumber?.trim());
    const isVkFilled = Boolean(contactVk?.trim());
    const phoneValid = !isPhoneFilled || isValidRussianPhone(contactNumber);
    const vkValid = !isVkFilled || isValidVk(contactVk);
    const isVkGreen = isVkFilled && isValidVk(contactVk);

    const step1Valid = petName.trim().length > 0 && Boolean(occurredDate);
    const step2Valid = locationLat != null && locationLng != null;
    const step3Valid = photos.length >= 1 && !photos.some(p => p.status === 'error' || p.status === 'uploading');
    const step4Valid = Boolean(contactName?.trim()) && (isPhoneFilled || isVkFilled) && phoneValid && vkValid;
    const step5Valid = true;

    const animationClass = isInitialMount.current
        ? "animate-fadeIn"
        : direction === "forward"
        ? "animate-wizardSlideForward"
        : "animate-wizardSlideBackward";

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose} initialFocus={initialFocusRef}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 flex items-stretch sm:items-center sm:justify-center sm:p-4 overflow-hidden overscroll-none">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95">
                        <Dialog.Panel className={`
                            relative w-full h-[100dvh] sm:h-[88vh] sm:max-h-[780px] sm:max-w-lg
                            bg-background-light dark:bg-background-dark
                            rounded-none sm:rounded-2xl
                            border-none sm:border sm:border-border-light sm:dark:border-border-dark
                            flex flex-col
                            overflow-hidden
                            overscroll-none
                            shadow-2xl
                        `}>
                            {/* Centered Error Notification Overlay (higher z-index) */}
                            <ReportProcessingErrorOverlay
                                error={step6Error}
                                onClose={() => setStep6Error(null)}
                                onChangePhotos={() => {
                                    setStep6Error(null);
                                    setStep(3);
                                }}
                                onRetry={handleStartSearch}
                            />

                            {showSuccess ? (
                                <ReportSuccessStep
                                    reportId={processedReport?.id || existingReport?.id || serverReportId}
                                    initialReport={processedReport || existingReport}
                                    reportType="lost"
                                    petName={processedReport?.pet?.name || processedReport?.pet_name || petName}
                                    occurredDate={processedReport?.event_occurred_at || occurredDate}
                                    photos={processedReport?.photos || photos}
                                    locationAddress={processedReport?.location_description || locationAddress}
                                    locationMunicipality={processedReport?.municipality || locationMunicipality}
                                    onClose={async () => {
                                        await deleteDraft('lost');
                                        handleClose();
                                    }}
                                />
                            ) : (
                                <>
                                    {/* Top Navigation & Integrated Stepper */}
                                    <div className="shrink-0 bg-background-light dark:bg-background-dark">
                                        <div className="px-4 py-2.5 flex items-center justify-between">
                                            <div className="w-8 flex items-center">
                                                {step > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={handleBack}
                                                        className="w-8 h-8 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                                                        aria-label={t('common.back')}
                                                        disabled={loading}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            <Dialog.Title className="flex-1 text-center font-medium text-sm sm:text-base text-foreground-light dark:text-foreground-dark truncate px-2">
                                                {headerContent.title}
                                            </Dialog.Title>
                                            <div className="w-8 flex justify-end">
                                                <button
                                                    ref={initialFocusRef}
                                                    onClick={handleClose}
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                                                    aria-label={t('common.close')}
                                                    disabled={loading}
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Stepper Progress Bar (no text, less rounded, less tall) */}
                                        <div className="grid grid-cols-6 gap-1 w-full px-4 pb-2">
                                            {Array.from({ length: totalSteps }).map((_, i) => {
                                                const stepNum = i + 1;
                                                const isCompleted = stepNum < step;
                                                const isCurrent = stepNum === step;
                                                return (
                                                    <div
                                                        key={i}
                                                        className={`h-1 rounded-sm transition-all duration-300 ${
                                                            isCompleted
                                                                ? "bg-primary"
                                                                : isCurrent
                                                                ? "bg-primary"
                                                                : "bg-neutral-200 dark:bg-neutral-700"
                                                        }`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto overflow-x-hidden bg-background-light dark:bg-background-dark flex flex-col min-w-0 w-full max-w-full overscroll-contain">
                                        <div className={`flex-1 flex flex-col min-w-0 w-full max-w-full overflow-x-hidden ${step === 2 ? 'p-0' : 'p-4 sm:p-5 pt-5 sm:pt-6'}`}>
                                            <div className={`flex-1 flex flex-col min-w-0 w-full max-w-full ${animationClass}`} key={step}>
                                                <style>{`
                                                    @keyframes smooth-scan { 0% { transform: translateY(-100%); } 100% { transform: translateY(200%); } }
                                                    .animate-smooth-scan { animation: smooth-scan 2s linear infinite; }
                                                    @keyframes pulse-grid { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.4; } }
                                                    .animate-pulse-grid { animation: pulse-grid 1s ease-in-out infinite; }
                                                    @keyframes bounceIn { 0% { opacity: 0; transform: scale(0.3); } 50% { opacity: 1; transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
                                                    .animate-bounceIn { animation: bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
                                                    @keyframes wizardSlideForward {
                                                        0% { opacity: 0; transform: translateX(25%); }
                                                        100% { opacity: 1; transform: translateX(0); }
                                                    }
                                                    @keyframes wizardSlideBackward {
                                                        0% { opacity: 0; transform: translateX(-25%); }
                                                        100% { opacity: 1; transform: translateX(0); }
                                                    }
                                                    .animate-wizardSlideForward { animation: wizardSlideForward 0.24s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                                                    .animate-wizardSlideBackward { animation: wizardSlideBackward 0.24s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                                                `}</style>
                                                {step === 1 && (
                                                    <div className="space-y-5">
                                                        {loadingPets ? (
                                                            <div className="flex justify-center items-center py-8">
                                                                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary"></div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {isAuthenticated && myPets.filter(p => !p.reported_as_lost_at).length > 0 && (
                                                                    <div className="space-y-2">
                                                                        <label className="block text-xs sm:text-sm font-normal mb-1.5 text-foreground-light dark:text-foreground-dark">
                                                                            {t('pets.select_missing_pet')}
                                                                        </label>
                                                                        <div className="flex gap-3 overflow-x-auto py-1 scrollbar-none touch-pan-x overscroll-x-contain">
                                                                            {myPets.filter(p => !p.reported_as_lost_at).map(pet => (
                                                                                <button
                                                                                    type="button"
                                                                                    key={pet.id}
                                                                                    onClick={() => { setSelectedPet(pet); setPetName(pet.name); }}
                                                                                    className="flex flex-col items-center gap-1.5 cursor-pointer group shrink-0 w-16 focus:outline-none"
                                                                                >
                                                                                    <div className={`relative w-12 h-12 rounded-full border-2 transition-all p-0.5 ${selectedPet?.id === pet.id ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'border-border-light dark:border-border-dark group-hover:border-primary/50'}`}>
                                                                                        {pet.profile_image_url ? (
                                                                                            <img
                                                                                                src={pet.profile_image_url.startsWith('http') ? pet.profile_image_url : `${BASE_URL}${pet.profile_image_url}`}
                                                                                                className="w-full h-full rounded-full object-cover"
                                                                                                alt={pet.name}
                                                                                            />
                                                                                        ) : (
                                                                                            <div className="w-full h-full rounded-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 text-subtle-light dark:text-subtle-dark font-medium text-sm">
                                                                                                {pet.name?.[0]}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <span className={`text-xs font-medium truncate w-full text-center ${selectedPet?.id === pet.id ? 'text-primary' : 'text-foreground-light dark:text-foreground-dark'}`}>
                                                                                        {pet.name}
                                                                                    </span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <Input
                                                                    label={t('pets.name_question')}
                                                                    value={petName}
                                                                    onChange={e => { setPetName(e.target.value); setSelectedPet(null); }}
                                                                    placeholder={t('pets.placeholders.name')}
                                                                />

                                                                <FriendlyDateSelector
                                                                    label={t('pets.when_missing')}
                                                                    value={occurredDate}
                                                                    onChange={setOccurredDate}
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {step === 2 && (
                                                    <div className="relative flex-1 w-full h-full min-h-[320px]">
                                                        {/* Top Floating Search Input with location icon & autocomplete */}
                                                        <div className="absolute top-3 inset-x-3 sm:top-4 sm:inset-x-4 z-30">
                                                            <MapLocationSearchInput
                                                                address={locationAddress}
                                                                onSelectPlace={handleSelectPlace}
                                                            />
                                                        </div>

                                                        {isMapLoaded ? (
                                                            <GoogleMap
                                                                mapContainerClassName="w-full h-full"
                                                                center={defaultCenter}
                                                                zoom={14}
                                                                onIdle={handleMapIdle}
                                                                onLoad={(map) => {
                                                                    mapRef.current = map;
                                                                    if (locationLat && locationLng) map.panTo({ lat: locationLat, lng: locationLng });
                                                                }}
                                                                options={{ disableDefaultUI: true, zoomControl: false, gestureHandling: 'greedy' }}
                                                            />
                                                        ) : (
                                                            <div className="flex items-center justify-center h-full text-subtle-light dark:text-subtle-dark text-sm font-medium">
                                                                {t('pets.loading_map')}
                                                            </div>
                                                        )}
                                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-10 drop-shadow-md">
                                                            <PetMapPinIcon className="w-10 h-10" color="#dc2626" />
                                                        </div>
                                                    </div>
                                                )}
                                                {step === 3 && (
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                        {photos.map((p, i) => (
                                                            <div
                                                                key={i}
                                                                className={`relative rounded-lg overflow-hidden border aspect-square shadow-sm group transition-all duration-300 ${
                                                                    p.status === 'error'
                                                                        ? 'border-danger-light bg-danger-light/10'
                                                                        : 'border-border-light dark:border-border-dark bg-neutral-100 dark:bg-card-dark'
                                                                }`}
                                                            >
                                                                <img
                                                                    src={p.previewUrl}
                                                                    className={`w-full h-full object-cover ${p.status === 'error' ? 'opacity-50 grayscale' : ''}`}
                                                                    alt=""
                                                                />

                                                                {/* UPLOADING */}
                                                                {p.status === 'uploading' && (
                                                                    <div className="absolute inset-0 z-10 overflow-hidden rounded-lg pointer-events-none">
                                                                        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[0.5px]" />
                                                                        <div className="absolute inset-0 z-10 opacity-30 animate-pulse-grid" style={{ backgroundImage: `linear-gradient(rgba(34, 211, 238, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.3) 1px, transparent 1px)`, backgroundSize: '10px 10px' }} />
                                                                        <div className="absolute inset-x-0 -top-full h-full z-20 animate-smooth-scan">
                                                                            <div className="h-full w-full bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
                                                                            <div className="absolute bottom-1/2 left-0 right-0 h-[2px] bg-cyan-400 shadow-[0_0_20px_rgba(34,211,238,1)]" />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* SUCCESS: GREEN CHECK */}
                                                                {p.status === 'success' && (
                                                                    <div className="absolute bottom-2 right-2 z-10 animate-bounceIn pointer-events-none">
                                                                        <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ERROR: RED CROSS */}
                                                                {p.status === 'error' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemovePhoto(i)}
                                                                        className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-red-50/10 backdrop-blur-[1px] cursor-pointer hover:bg-red-50/20 transition-colors w-full h-full"
                                                                    >
                                                                        <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center mb-1 animate-bounceIn shadow-sm">
                                                                            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                        </div>
                                                                        <span className="text-xs text-danger-light font-medium bg-white/90 px-2 py-0.5 rounded-full shadow-sm mt-0.5">Not Allowed</span>
                                                                    </button>
                                                                )}

                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemovePhoto(i)}
                                                                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-danger-light transition-colors z-20 text-xs"
                                                                    aria-label="Remove photo"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {photos.length < maxPhotos && (
                                                            <label className="flex flex-col items-center justify-center w-full aspect-square rounded-lg border-2 border-dashed border-border-light dark:border-border-dark hover:border-primary cursor-pointer transition-colors text-subtle-light dark:text-subtle-dark hover:text-primary bg-neutral-50/50 dark:bg-card-dark/30">
                                                                <svg className="w-6 h-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                <span className="text-xs font-medium">{t('pets.add_photo')}</span>
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    multiple
                                                                    className="hidden"
                                                                    onChange={e => {
                                                                        const files = Array.from(e.target.files || []);
                                                                        if (files.length > 0) handleTempPhotoChange(files);
                                                                        e.target.value = "";
                                                                    }}
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                )}
                                                {step === 4 && (
                                                    <div className="space-y-5">
                                                        <Input
                                                            label={
                                                                <>
                                                                    {t('pets.contact_name', { defaultValue: 'Contact Name' })}
                                                                    <span className="text-danger-light dark:text-danger-dark ml-1">*</span>
                                                                </>
                                                            }
                                                            startIcon={<UserIcon className="w-4 h-4" />}
                                                            value={contactName}
                                                            onChange={e => setContactName(e.target.value)}
                                                        />
                                                        <Input
                                                            label={t('pets.contact_number', { defaultValue: 'Contact Number' })}
                                                            startIcon={<PhoneIcon className="w-4 h-4" />}
                                                            value={contactNumber}
                                                            onChange={e => setContactNumber(e.target.value)}
                                                            error={isPhoneFilled && !phoneValid}
                                                            endIcon={
                                                                isPhoneFilled ? (
                                                                    phoneValid ? (
                                                                        <CheckFilledIcon className="w-5 h-5 text-primary animate-check-pop" />
                                                                    ) : (
                                                                        <ExclamationFilledIcon className="w-5 h-5 text-danger-light animate-fadeIn" />
                                                                    )
                                                                ) : null
                                                            }
                                                        />
                                                        <div className="space-y-2">
                                                            <Input
                                                                label={t('pets.contact_vk', { defaultValue: 'VK Profile' })}
                                                                startIcon={<VkInputIcon className="w-5 h-5" />}
                                                                value={contactVk}
                                                                onChange={e => setContactVk(e.target.value)}
                                                                error={isVkFilled && !vkValid}
                                                                endIcon={
                                                                    isVkFilled ? (
                                                                        vkValid ? (
                                                                            <CheckFilledIcon className="w-5 h-5 text-primary animate-check-pop" />
                                                                        ) : (
                                                                            <ExclamationFilledIcon className="w-5 h-5 text-danger-light animate-fadeIn" />
                                                                        )
                                                                    ) : null
                                                                }
                                                                placeholder={t('pets.placeholders.contact_vk', { defaultValue: 'https://vk.ru/..., @usuario o id123456' })}
                                                            />
                                                            <div className="pt-0.5">
                                                                <Switch
                                                                    checked={vkCoauthor}
                                                                    onChange={setVkCoauthor}
                                                                    disabled={!isVkGreen}
                                                                    label={t('pets.contact_vk_coauthor', { defaultValue: 'Invitarme como coautor en VK' })}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {step === 5 && (
                                                    <div className="space-y-5">
                                                        <Textarea
                                                            label={
                                                                <span>
                                                                    {t('pets.user_description', { defaultValue: 'Comentario' })}
                                                                    <span className="text-subtle-light dark:text-subtle-dark font-normal ml-1.5 text-xs sm:text-sm">
                                                                        ({t('pets.optional_label', { defaultValue: 'opcional' })})
                                                                    </span>
                                                                </span>
                                                            }
                                                            rows={4}
                                                            className="min-h-24 sm:min-h-28"
                                                            value={userDescription}
                                                            onChange={e => setUserDescription(e.target.value)}
                                                            placeholder={t('pets.placeholders.user_description', { defaultValue: 'Comentario adicional para mostrar en la publicación para ayudar...' })}
                                                        />
                                                        <Input
                                                            label={
                                                                <span>
                                                                    {t('pets.feat_collar_label', { defaultValue: 'Collar' })}
                                                                    <span className="text-subtle-light dark:text-subtle-dark font-normal ml-1.5 text-xs sm:text-sm">
                                                                        ({t('pets.optional_label', { defaultValue: 'opcional' })})
                                                                    </span>
                                                                </span>
                                                            }
                                                            value={featCollarText}
                                                            onChange={e => setFeatCollarText(e.target.value)}
                                                        />
                                                        <Input
                                                            label={
                                                                <span>
                                                                    {t('pets.feat_clothes_label', { defaultValue: 'Ropa' })}
                                                                    <span className="text-subtle-light dark:text-subtle-dark font-normal ml-1.5 text-xs sm:text-sm">
                                                                        ({t('pets.optional_label', { defaultValue: 'opcional' })})
                                                                    </span>
                                                                </span>
                                                            }
                                                            value={featClothesText}
                                                            onChange={e => setFeatClothesText(e.target.value)}
                                                        />
                                                        <Input
                                                            label={
                                                                <span>
                                                                    {t('pets.feat_microchip_label', { defaultValue: 'Microchip' })}
                                                                    <span className="text-subtle-light dark:text-subtle-dark font-normal ml-1.5 text-xs sm:text-sm">
                                                                        ({t('pets.optional_label', { defaultValue: 'opcional' })})
                                                                    </span>
                                                                </span>
                                                            }
                                                            value={featMicrochipText}
                                                            onChange={e => setFeatMicrochipText(e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                                {step === 6 && (
                                                    <div className="flex flex-col h-full justify-between py-1">
                                                        <div className="space-y-4 py-1">
                                                            <div className="flex items-start gap-3">
                                                                <CheckCircleFilledIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-xs sm:text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                                                                        {t('pets.step5_point1_title')}
                                                                    </h4>
                                                                    <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5 leading-relaxed">
                                                                        {t('pets.step5_point1_desc')}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-start gap-3">
                                                                <CheckCircleFilledIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-xs sm:text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                                                                        {t('pets.step5_point2_title')}
                                                                    </h4>
                                                                    <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5 leading-relaxed">
                                                                        {t('pets.step5_point2_desc')}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-start gap-3">
                                                                <CheckCircleFilledIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-xs sm:text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                                                                        {t('pets.step5_point3_title')}
                                                                    </h4>
                                                                    <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5 leading-relaxed">
                                                                        {t('pets.step5_point3_desc')}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-start gap-3">
                                                                <CheckCircleFilledIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-xs sm:text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                                                                        {t('pets.step5_point4_title')}
                                                                    </h4>
                                                                    <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5 leading-relaxed">
                                                                        {t('pets.step5_point4_desc')}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-start gap-3">
                                                                <CheckCircleFilledIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-xs sm:text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                                                                        {t('pets.step5_point5_title')}
                                                                    </h4>
                                                                    <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5 leading-relaxed">
                                                                        {t('pets.step5_point5_desc')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="pt-4 mt-auto">
                                                            {isAuthenticated ? (
                                                                <div className="w-full">
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleStartSearch}
                                                                        variant="magic"
                                                                        size="md"
                                                                        loading={loading}
                                                                        disabled={loading}
                                                                        className="w-full font-medium sm:!py-2 sm:!text-sm"
                                                                    >
                                                                        {loading
                                                                            ? t('pets.starting_search', { defaultValue: 'Iniciando búsqueda con IA...' })
                                                                            : step6Error
                                                                                ? t('pets.retry_search', { defaultValue: 'Reintentar búsqueda' })
                                                                                : t('pets.start_search', { defaultValue: 'Iniciar búsqueda' })}
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <div className="w-full">
                                                                    <Button
                                                                        onClick={handleFinishAndLogin}
                                                                        variant="magic"
                                                                        size="md"
                                                                        className="w-full font-medium sm:!py-2 sm:!text-sm"
                                                                    >
                                                                        {t('pets.step5_btn')}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {step < 6 && (
                                        <div className="bg-white dark:bg-background-dark px-4 sm:px-6 py-3 border-t border-border-light dark:border-border-dark shrink-0 pb-safe">
                                            <div className="grid grid-cols-2 gap-3 w-full">
                                                <Button
                                                    type="button"
                                                    variant="neutral"
                                                    size="md"
                                                    onClick={() => { if (step > 1) handleBack(); else handleClose(); }}
                                                    className="w-full font-medium sm:!py-2 sm:!text-sm"
                                                >
                                                    {step > 1 ? t('common.back') : t('common.cancel')}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    size="md"
                                                    onClick={handleNext}
                                                    disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid) || (step === 3 && !step3Valid) || (step === 4 && !step4Valid)}
                                                    className="w-full font-medium sm:!py-2 sm:!text-sm"
                                                >
                                                    {t('common.next')}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
